import { MetricCard } from "design-system/organisms/insights/MetricCard"
import { formatKnowledgeRate } from "modules/backoffice/lib/knowledge/formatKnowledgeRate"
import { useTranslation } from "react-i18next"
import { BackofficeKnowledgeMetricsResponseDataSchema } from "schema"
import { z } from "zod"

type Metrics = z.infer<typeof BackofficeKnowledgeMetricsResponseDataSchema>

type Props = {
  total: Metrics | null | undefined
  isPending: boolean
}

export function BackofficeKnowledgeHeadline({ total, isPending }: Props) {
  const { t } = useTranslation()

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <MetricCard
        title={t("backoffice.knowledge.metrics.knowledgeCoverageRate")}
        tooltip={t("backoffice.knowledge.metrics.knowledgeCoverageRateHelp")}
        currentValue={formatKnowledgeRate(total?.knowledgeCoverageRate)}
        metric={t("backoffice.knowledge.headline.coverageSplit", {
          knowledgeBase: formatKnowledgeRate(total?.knowledgeBaseCoverageRate),
          capabilities: formatKnowledgeRate(total?.capabilityCoverageRate),
        })}
        metricTooltip={t("backoffice.knowledge.headline.coverageSplitHelp")}
        isPending={isPending}
        className="border-primary"
      />
      <MetricCard
        title={t("backoffice.knowledge.metrics.interimCoverageRate")}
        tooltip={t("backoffice.knowledge.metrics.interimCoverageRateHelp")}
        currentValue={formatKnowledgeRate(total?.interimCoverageRate)}
        metric={t("backoffice.knowledge.headline.answeredVersusGaps", {
          answered: total?.answeredCount ?? 0,
          gaps: total?.knowledgeGapCount ?? 0,
        })}
        isPending={isPending}
      />
      <MetricCard
        title={t("backoffice.knowledge.metrics.answeredRate")}
        tooltip={t("backoffice.knowledge.metrics.answeredRateHelp")}
        currentValue={formatKnowledgeRate(total?.answeredRate)}
        isPending={isPending}
      />
      <MetricCard
        title={t("backoffice.knowledge.metrics.knowledgeUnansweredRate")}
        tooltip={t("backoffice.knowledge.metrics.knowledgeUnansweredRateHelp")}
        currentValue={formatKnowledgeRate(total?.knowledgeUnansweredRate)}
        isPending={isPending}
      />
      <MetricCard
        title={t("backoffice.knowledge.metrics.retrievalMissRate")}
        tooltip={t("backoffice.knowledge.metrics.retrievalMissRateHelp")}
        currentValue={formatKnowledgeRate(total?.retrievalMissRate)}
        isPending={isPending}
      />
      <MetricCard
        title={t("backoffice.knowledge.metrics.unattributedAnswerRate")}
        tooltip={t("backoffice.knowledge.metrics.unattributedAnswerRateHelp")}
        currentValue={formatKnowledgeRate(total?.unattributedAnswerRate)}
        isPending={isPending}
      />
      <MetricCard
        title={t("backoffice.knowledge.metrics.questionCount")}
        tooltip={t("backoffice.knowledge.metrics.questionCountHelp")}
        currentValue={total ? String(total.questionCount) : "—"}
        isPending={isPending}
      />
    </div>
  )
}
